import { useState, useMemo, useCallback } from "react";
import { GitCompare, AlertCircle, RotateCcw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { numberRuns } from "@/lib/run-trend";
import { isStrength } from "@/lib/run-findings";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tonal } from "@/components/ui/tonal";

import type {
  ComparisonMode,
  FindingsFilter,
  JourneyFilter,
  SelectedStepComparison,
  ComparisonFinding,
  ComparisonRunInfo,
} from "./comparison/comparison-types";
import { ComparisonControlBar } from "./comparison/comparison-control-bar";
import { JourneyTraceDiff } from "./comparison/journey-trace-diff";
import { FindingsDeltaView } from "./comparison/findings-delta-view";
import { ScreenshotCompareDialog } from "./comparison/screenshot-compare-dialog";

export interface RunComparisonViewProps {
  currentWalkthrough: ComparisonRunInfo & { scenarioId?: string };
  currentRunNumber?: number;
  scenarioRuns: ComparisonRunInfo[];
  runsChronological: ComparisonRunInfo[];
  activeBaseRunId: string | null;
  onSelectBaseRun: (runId: string) => void;
  onOpenFinding: (findingId: string) => void;
  onCreateIssue?: (canonicalId: string | null) => void;
  onLinkIssue?: (canonicalId: string) => void;
  onBulkCreateIssues?: (canonicalIds: string[]) => void;
  canEdit: boolean;
  projectId: string;
  isTrackerConfigured?: boolean;
}

export function RunComparisonView({
  currentWalkthrough,
  currentRunNumber,
  scenarioRuns,
  runsChronological,
  activeBaseRunId,
  onSelectBaseRun,
  onOpenFinding,
  onCreateIssue,
  onLinkIssue,
  onBulkCreateIssues,
  canEdit,
  isTrackerConfigured,
}: RunComparisonViewProps) {
  const [mode, setMode] = useState<ComparisonMode>("journey");
  const [findingsFilter, setFindingsFilter] = useState<FindingsFilter>("all");
  const [journeyFilter, setJourneyFilter] = useState<JourneyFilter>("all");
  const [copiedMd, setCopiedMd] = useState(false);
  const [selectedStepComparison, setSelectedStepComparison] =
    useState<SelectedStepComparison | null>(null);

  const runNumbers = useMemo(() => numberRuns(scenarioRuns), [scenarioRuns]);
  const candidateRuns = useMemo(
    () => runsChronological.filter((r) => r.id !== currentWalkthrough.id),
    [runsChronological, currentWalkthrough.id],
  );
  const baseRunNumber = activeBaseRunId
    ? runNumbers.get(activeBaseRunId)
    : undefined;

  const compareQuery = trpc.walkthrough.compare.useQuery(
    {
      baseWalkthroughId: activeBaseRunId ?? "",
      compareWalkthroughId: currentWalkthrough.id,
    },
    {
      enabled:
        Boolean(activeBaseRunId) &&
        Boolean(currentWalkthrough.id) &&
        activeBaseRunId !== currentWalkthrough.id,
      staleTime: 60_000,
    },
  );

  const handleCopyMarkdown = useCallback(() => {
    if (!compareQuery.data?.markdownSummary) return;
    navigator.clipboard.writeText(compareQuery.data.markdownSummary);
    setCopiedMd(true);
    setTimeout(() => setCopiedMd(false), 2000);
  }, [compareQuery.data?.markdownSummary]);

  const journeyDiff = compareQuery.data?.journeyDiff;
  const filteredJourneySteps = useMemo(() => {
    if (!journeyDiff?.steps) return [];
    if (journeyFilter === "all") return journeyDiff.steps;
    if (journeyFilter === "deltas") {
      return journeyDiff.steps.filter((s) => s.status !== "same");
    }
    return journeyDiff.steps.filter((s) => s.status === journeyFilter);
  }, [journeyDiff?.steps, journeyFilter]);

  const allNewFindings = (compareQuery.data?.newFindings ??
    []) as ComparisonFinding[];
  const newProblems = useMemo(
    () => allNewFindings.filter((f) => !isStrength(f.type)),
    [allNewFindings],
  );
  const newStrengths = useMemo(
    () => allNewFindings.filter((f) => isStrength(f.type)),
    [allNewFindings],
  );
  const allResolvedFindings = (compareQuery.data?.resolvedFindings ??
    []) as ComparisonFinding[];
  const resolvedProblems = useMemo(
    () => allResolvedFindings.filter((f) => !isStrength(f.type)),
    [allResolvedFindings],
  );
  const lostStrengths = useMemo(
    () => allResolvedFindings.filter((f) => isStrength(f.type)),
    [allResolvedFindings],
  );

  if (scenarioRuns.length < 2) {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-4 py-12"
      >
        <div className="flex justify-center">
          <div className="rounded-full bg-muted p-3">
            <GitCompare className="size-6 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">
            Run comparison requires at least two runs
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            This scenario has only run once. Re-run this scenario to track how
            findings, regressions, and fixes progress across runs.
          </p>
        </div>
      </Tonal>
    );
  }

  const summary = compareQuery.data?.summary;
  const newCount = summary?.newCount ?? 0;
  const resolvedCount = resolvedProblems.length;
  const changedCount = summary?.changedCount ?? 0;
  const persistentCount = summary?.persistentCount ?? 0;
  const totalDiffs =
    newCount + resolvedProblems.length + lostStrengths.length + changedCount;

  const journeyStats = journeyDiff?.stats;
  const totalJourneyDeltas =
    (journeyStats?.resolved ?? 0) +
    (journeyStats?.newPath ?? 0) +
    (journeyStats?.regressions ?? 0) +
    (journeyStats?.persists ?? 0) +
    (journeyStats?.bypassed ?? 0) +
    (journeyStats?.stalledInBaseline ?? 0);

  const baseStepCount = compareQuery.data?.baseWalkthrough?.stepCount ?? 0;
  const compareStepCount =
    compareQuery.data?.compareWalkthrough?.stepCount ?? 0;
  const hasStepDiscrepancy =
    compareQuery.data &&
    (baseStepCount === 0 ||
      compareStepCount === 0 ||
      Math.abs(baseStepCount - compareStepCount) >= 2);

  return (
    <div className="space-y-5">
      <ComparisonControlBar
        candidateRuns={candidateRuns}
        activeBaseRunId={activeBaseRunId}
        onSelectBaseRun={onSelectBaseRun}
        runNumbers={runNumbers}
        currentRunNumber={currentRunNumber}
        mode={mode}
        setMode={setMode}
        journeyStepCount={journeyStats?.totalAlignedSteps}
        totalDiffs={totalDiffs}
        markdownSummary={compareQuery.data?.markdownSummary}
        onCopyMarkdown={handleCopyMarkdown}
        copiedMd={copiedMd}
      />

      {hasStepDiscrepancy && (
        <div className="flex items-start gap-2.5 rounded-lg border border-border/80 bg-muted/30 p-3 text-xs text-muted-foreground">
          <AlertCircle className="size-4 shrink-0 text-muted-foreground mt-0.5" />
          <div>
            <span className="font-semibold text-foreground">
              Step coverage variance:
            </span>{" "}
            Baseline Run #{baseRunNumber ?? "baseline"} recorded {baseStepCount}{" "}
            {baseStepCount === 1 ? "step" : "steps"}, while current Run #
            {currentRunNumber ?? "current"} recorded {compareStepCount}{" "}
            {compareStepCount === 1 ? "step" : "steps"}.
            {compareStepCount === 0 &&
              " The current run terminated without recording steps."}
            {baseStepCount === 0 &&
              " The baseline run terminated without recording steps."}
          </div>
        </div>
      )}

      {compareQuery.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-md" />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      )}

      {compareQuery.isError && (
        <Tonal
          padding="md"
          className="rounded-lg border border-destructive/30 space-y-2"
        >
          <div className="text-sm font-semibold text-destructive">
            Comparison unavailable
          </div>
          <p className="text-sm text-destructive">
            {compareQuery.error.message || "Failed to compare runs."}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => compareQuery.refetch()}
          >
            <RotateCcw className="mr-1.5 size-3.5" />
            Retry comparison
          </Button>
        </Tonal>
      )}

      {compareQuery.data && (
        <div className="space-y-5">
          {mode === "journey" && journeyDiff && (
            <JourneyTraceDiff
              journeyDiff={journeyDiff}
              filteredSteps={filteredJourneySteps}
              journeyFilter={journeyFilter}
              setJourneyFilter={setJourneyFilter}
              totalJourneyDeltas={totalJourneyDeltas}
              baseRunNumber={baseRunNumber}
              currentRunNumber={currentRunNumber}
              activeBaseRunId={activeBaseRunId}
              currentWalkthroughId={currentWalkthrough.id}
              onOpenFinding={onOpenFinding}
              onSelectStepComparison={setSelectedStepComparison}
            />
          )}

          {mode === "findings" && (
            <FindingsDeltaView
              newProblems={newProblems}
              newStrengths={newStrengths}
              resolvedFindings={resolvedProblems}
              lostStrengths={lostStrengths}
              changedSeverity={
                compareQuery.data.changedSeverity as Array<{
                  base: ComparisonFinding;
                  compare: ComparisonFinding;
                }>
              }
              persistentFindings={
                compareQuery.data.persistentFindings as Array<{
                  base: ComparisonFinding;
                  compare: ComparisonFinding;
                }>
              }
              findingsFilter={findingsFilter}
              setFindingsFilter={setFindingsFilter}
              totalDiffs={totalDiffs}
              resolvedCount={resolvedCount}
              changedCount={changedCount}
              persistentCount={persistentCount}
              baseRunNumber={baseRunNumber}
              currentRunNumber={currentRunNumber}
              canEdit={canEdit}
              isTrackerConfigured={isTrackerConfigured}
              onBulkCreateIssues={onBulkCreateIssues}
              onOpenFinding={onOpenFinding}
              onCreateIssue={onCreateIssue}
              onLinkIssue={onLinkIssue}
            />
          )}
        </div>
      )}

      <ScreenshotCompareDialog
        selectedStepComparison={selectedStepComparison}
        onClose={() => setSelectedStepComparison(null)}
        baseRunNumber={baseRunNumber}
        currentRunNumber={currentRunNumber}
        activeBaseRunId={activeBaseRunId}
        currentWalkthroughId={currentWalkthrough.id}
      />
    </div>
  );
}
